// ============================================================================
// OPTIMAL SPARES ENGINE
// Z-score ROP, Stocking Policy Matrix, Weibull Demand, API Bridge
// Bridges OptimalAvailability ↔ spare.optimalworld.org
// ============================================================================

// ---------------------------------------------------------------------------
// Z-SCORE LOOKUP TABLE (service level → z-value)
// ---------------------------------------------------------------------------
// OAV-029 (final): extended Z-score (standard-normal inverse-CDF) lookup table —
// 41-point grid from 0.50 to 0.999 plus an analytical Beasley-Springer-Moro
// fallback for arbitrary service levels. Removes interpolation error at high SLs.
const Z_LOOKUP = {
  0.50: 0.000, 0.55: 0.126, 0.60: 0.253, 0.65: 0.385, 0.70: 0.524,
  0.72: 0.583, 0.75: 0.674, 0.78: 0.772, 0.80: 0.842, 0.82: 0.915,
  0.84: 0.994, 0.85: 1.036, 0.86: 1.080, 0.88: 1.175, 0.89: 1.227,
  0.90: 1.282, 0.91: 1.341, 0.92: 1.405, 0.93: 1.476, 0.94: 1.555,
  0.95: 1.645, 0.96: 1.751, 0.97: 1.881, 0.975: 1.960, 0.98: 2.054,
  0.985: 2.170, 0.99: 2.326, 0.992: 2.409, 0.994: 2.512, 0.995: 2.576,
  0.996: 2.652, 0.997: 2.748, 0.998: 2.878, 0.999: 3.090, 0.9995: 3.291,
  0.9999: 3.719
};

// Beasley-Springer-Moro inverse-normal-CDF approximation (max error ~ 1.4e-9)
function normalInverse(p) {
  if (p <= 0 || p >= 1) {
    if (p === 0) return -Infinity;
    if (p === 1) return Infinity;
    throw new Error('normalInverse: p must be in (0, 1)');
  }
  const a = [-3.969683028665376e+01,  2.209460984245205e+02, -2.759285104469687e+02,
              1.383577518672690e+02, -3.066479806614716e+01,  2.506628277459239e+00];
  const b = [-5.447609879822406e+01,  1.615858368580409e+02, -1.556989798598866e+02,
              6.680131188771972e+01, -1.328068155288572e+01];
  const c = [-7.784894002430293e-03, -3.223964580411365e-01, -2.400758277161838e+00,
             -2.549732539343734e+00,  4.374664141464968e+00,  2.938163982698783e+00];
  const d = [ 7.784695709041462e-03,  3.224671290700398e-01,  2.445134137142996e+00,
              3.754408661907416e+00];
  const pl = 0.02425, ph = 1 - pl;
  let q, r;
  if (p < pl) {
    q = Math.sqrt(-2 * Math.log(p));
    return (((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) /
           ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);
  } else if (p <= ph) {
    q = p - 0.5; r = q*q;
    return (((((a[0]*r+a[1])*r+a[2])*r+a[3])*r+a[4])*r+a[5])*q /
           (((((b[0]*r+b[1])*r+b[2])*r+b[3])*r+b[4])*r+1);
  } else {
    q = Math.sqrt(-2 * Math.log(1 - p));
    return -(((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) /
            ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);
  }
}

function getZScore(serviceLevel) {
  // OAV-029 (final): exact lookup if grid hit, otherwise analytical normalInverse.
  // Linear interpolation kept as safety net for partial / sparse tables.
  if (Z_LOOKUP[serviceLevel] !== undefined) return Z_LOOKUP[serviceLevel];
  if (serviceLevel > 0 && serviceLevel < 1) {
    try { return normalInverse(serviceLevel); } catch (_) { /* fall through */ }
  }
  const keys = Object.keys(Z_LOOKUP).map(Number).sort((a, b) => a - b);
  for (let i = 0; i < keys.length - 1; i++) {
    if (serviceLevel >= keys[i] && serviceLevel <= keys[i + 1]) {
      const frac = (serviceLevel - keys[i]) / (keys[i + 1] - keys[i]);
      return Z_LOOKUP[keys[i]] + frac * (Z_LOOKUP[keys[i + 1]] - Z_LOOKUP[keys[i]]);
    }
  }
  return serviceLevel >= 0.999 ? 3.09 : 0.0;
}

// ---------------------------------------------------------------------------
// DEFAULT STOCKING POLICY MATRIX (4-tier, matches OptimalSPARES API)
// ---------------------------------------------------------------------------
const DEFAULT_STOCKING_POLICY = {
  safety_critical: {
    label: 'Safety-Critical',
    action: 'always_stock',
    minStockRule: '1x failure qty',
    maxStockRule: '2x failure qty',
    serviceLevelPct: 99.0,
    leadTimeTolDays: 0,
    color: '#991B1B',
    bgColor: '#FEE2E2',
    description: 'Must be stocked at all times. Zero tolerance for stockout. Insurance spares.'
  },
  production_critical: {
    label: 'Production-Critical',
    action: 'stock_if_leadtime_over_tolerance',
    minStockRule: 'Statistical failure rate',
    maxStockRule: '12m consumption',
    serviceLevelPct: 95.0,
    leadTimeTolDays: 14,
    color: '#92400E',
    bgColor: '#FEF3C7',
    description: 'Stock if lead time exceeds 14 days. Statistical demand-driven quantities.'
  },
  standard: {
    label: 'Standard',
    action: 'order_on_demand_if_leadtime_under_4w',
    minStockRule: '0',
    maxStockRule: '6m consumption',
    serviceLevelPct: 85.0,
    leadTimeTolDays: 28,
    color: '#1E40AF',
    bgColor: '#DBEAFE',
    description: 'JIT ordering if lead time under 28 days. Minimal on-hand inventory.'
  },
  non_critical: {
    label: 'Non-Critical',
    action: 'supplier_managed_vmi',
    minStockRule: '0',
    maxStockRule: '0',
    serviceLevelPct: 70.0,
    leadTimeTolDays: 56,
    color: '#6B7280',
    bgColor: '#F3F4F6',
    description: 'Vendor-managed inventory (VMI). No on-hand stock required.'
  }
};

// ---------------------------------------------------------------------------
// CRITICALITY CLASSIFIER
// Maps severity × consequence to stocking tier
// ---------------------------------------------------------------------------
function classifyCriticality(spare, linkedFailureModes = []) {
  const maxSeverity = linkedFailureModes.length > 0
    ? Math.max(...linkedFailureModes.map(fm => parseInt(fm.severity) || 5))
    : parseInt(spare.criticalityRating) || 5;

  // Check for safety/environmental consequences
  const hasSafety = linkedFailureModes.some(fm =>
    (fm.consequence || '').toLowerCase().includes('safety') ||
    (fm.consequence || '').toLowerCase().includes('injury') ||
    (fm.consequence || '').toLowerCase().includes('fatality') ||
    parseInt(fm.severity) >= 9
  );
  const hasEnvironmental = linkedFailureModes.some(fm =>
    (fm.consequence || '').toLowerCase().includes('environment') ||
    (fm.consequence || '').toLowerCase().includes('spill') ||
    parseInt(fm.severity) >= 8
  );

  if (hasSafety || hasEnvironmental || maxSeverity >= 9) return 'safety_critical';
  if (maxSeverity >= 7) return 'production_critical';
  if (maxSeverity >= 4) return 'standard';
  return 'non_critical';
}

// ---------------------------------------------------------------------------
// Z-SCORE REORDER POINT CALCULATION
// Full implementation matching OptimalSPARES routes_enhancements.py
// ---------------------------------------------------------------------------
function calculateROP(spare, demandHistory = [], serviceLevel = null, policy = null) {
  const tier = policy || DEFAULT_STOCKING_POLICY.standard;
  const sl = serviceLevel || (tier.serviceLevelPct / 100) || 0.95;
  const z = getZScore(sl);

  // Demand statistics
  let avgMonthlyDemand, demandStdDev, avgLeadTimeDays, leadTimeStdDev;

  if (demandHistory.length >= 3) {
    // Use actual demand history
    const quantities = demandHistory.map(d => d.quantity || 0);
    const leadTimes = demandHistory.map(d => d.leadTimeDays || parseFloat(spare.leadTime) || 30);
    avgMonthlyDemand = quantities.reduce((s, q) => s + q, 0) / quantities.length;
    demandStdDev = Math.sqrt(quantities.reduce((s, q) => s + Math.pow(q - avgMonthlyDemand, 2), 0) / quantities.length);
    avgLeadTimeDays = leadTimes.reduce((s, l) => s + l, 0) / leadTimes.length;
    leadTimeStdDev = Math.sqrt(leadTimes.reduce((s, l) => s + Math.pow(l - avgLeadTimeDays, 2), 0) / leadTimes.length);
  } else {
    // Estimate from failure rates and mission data
    const annualDemand = parseFloat(spare.annualDemand) || parseFloat(spare.expectedDemand) || 1;
    avgMonthlyDemand = annualDemand / 12;
    demandStdDev = avgMonthlyDemand * 0.3; // Assume 30% CV when no history
    avgLeadTimeDays = parseFloat(spare.leadTime) || 30;
    leadTimeStdDev = avgLeadTimeDays * 0.2; // Assume 20% CV for lead time
  }

  // Core Z-score ROP formula:
  // ROP = D̄ × L̄ + Z × √(σ_d² × L̄ + σ_LT² × D̄²)
  const dailyDemand = avgMonthlyDemand / 30;
  const demandDuringLeadTime = dailyDemand * avgLeadTimeDays;

  const safetyStockComponent = Math.sqrt(
    Math.pow(demandStdDev, 2) * avgLeadTimeDays +
    Math.pow(leadTimeStdDev, 2) * Math.pow(dailyDemand, 2)
  );
  const safetyStock = z * safetyStockComponent;
  const reorderPoint = Math.ceil(demandDuringLeadTime + safetyStock);

  // Min/max based on policy
  const currentStock = parseInt(spare.quantity) || parseInt(spare.stockLevel) || 0;
  let recommendation;
  if (currentStock >= reorderPoint * 1.1) recommendation = 'LOWER';
  else if (currentStock <= reorderPoint * 0.9) recommendation = 'RAISE';
  else recommendation = 'HOLD';

  return {
    inputs: {
      avgMonthlyDemand: avgMonthlyDemand.toFixed(2),
      annualDemand: (avgMonthlyDemand * 12).toFixed(1),
      demandStdDev: demandStdDev.toFixed(3),
      avgLeadTimeDays: avgLeadTimeDays.toFixed(1),
      leadTimeStdDev: leadTimeStdDev.toFixed(2),
      serviceLevel: (sl * 100).toFixed(1),
      zScore: z.toFixed(3),
      hasHistory: demandHistory.length >= 3
    },
    results: {
      demandDuringLeadTime: demandDuringLeadTime.toFixed(2),
      safetyStock: Math.ceil(safetyStock),
      reorderPoint,
      currentStock,
      recommendation
    },
    formula: `ROP = (${dailyDemand.toFixed(3)}/day × ${avgLeadTimeDays.toFixed(0)}d) + ${z.toFixed(2)} × √(${demandStdDev.toFixed(3)}² × ${avgLeadTimeDays.toFixed(0)} + ${leadTimeStdDev.toFixed(2)}² × ${dailyDemand.toFixed(3)}²) = ${reorderPoint}`
  };
}

// ---------------------------------------------------------------------------
// WEIBULL-BASED DEMAND FORECASTING
// Uses Weibull parameters from linked equipment to predict future demand
// ---------------------------------------------------------------------------
function weibullDemandForecast(spare, linkedEquipment = [], weibullSets = [], horizon = 12) {
  // Find applicable Weibull sets for linked equipment
  const applicableSets = [];
  linkedEquipment.forEach(eq => {
    const ws = weibullSets.find(w =>
      w.equipmentId === eq.id || w.description?.includes(eq.description)
    );
    if (ws && ws.beta > 0 && ws.eta > 0) {
      applicableSets.push({ equipment: eq, beta: ws.beta, eta: ws.eta });
    }
  });

  if (applicableSets.length === 0) return null;

  // For each linked equipment with Weibull params, forecast failures over horizon
  let totalExpectedDemand = 0;
  const forecasts = applicableSets.map(({ equipment, beta, eta }) => {
    const installDate = equipment.installDate ? new Date(equipment.installDate) : new Date(Date.now() - 5 * 365.25 * 24 * 3600000);
    const currentAgeHours = (Date.now() - installDate.getTime()) / (3600000);

    // Expected failures in next horizon months using Weibull renewal rate
    // E[N(t, t+Δ)] ≈ (β/η) × ((t+Δ)/η)^(β-1) × Δ  for renewal process approximation
    const horizonHours = horizon * 730; // ~hours per month
    const t = currentAgeHours;
    const dt = horizonHours;

    // Instantaneous failure rate h(t) = (β/η)(t/η)^(β-1)
    const hazardRate = (beta / eta) * Math.pow(t / eta, beta - 1);
    const expectedFailures = hazardRate * dt;
    const qtyPerAssembly = parseFloat(spare.qtyPerAssembly) || 1;

    totalExpectedDemand += expectedFailures * qtyPerAssembly;

    return {
      equipmentId: equipment.id,
      equipmentDesc: equipment.description,
      beta,
      eta,
      currentAgeHours: Math.round(currentAgeHours),
      hazardRate: hazardRate.toFixed(6),
      expectedFailures: expectedFailures.toFixed(2),
      qtyPerFailure: qtyPerAssembly,
      expectedDemand: (expectedFailures * qtyPerAssembly).toFixed(2)
    };
  });

  return {
    method: 'Weibull renewal rate',
    horizonMonths: horizon,
    totalExpectedDemand: totalExpectedDemand.toFixed(2),
    monthlyDemand: (totalExpectedDemand / horizon).toFixed(3),
    forecasts
  };
}

// ---------------------------------------------------------------------------
// POISSON FILL RATE CALCULATION (retained from original)
// ---------------------------------------------------------------------------
function poissonFillRate(lambda, stock) {
  let cumProb = 0;
  let term = Math.exp(-lambda);
  for (let k = 0; k <= stock; k++) {
    cumProb += term;
    term *= lambda / (k + 1);
  }
  return Math.min(1, cumProb);
}

function poissonRecommendedStock(lambda, targetFillRate = 0.95) {
  let stock = 0;
  let cumProb = 0;
  let term = Math.exp(-lambda);
  while (cumProb < targetFillRate && stock < 200) {
    cumProb += term;
    term *= lambda / (stock + 1);
    if (cumProb < targetFillRate) stock++;
  }
  return stock;
}

// ---------------------------------------------------------------------------
// FULL SPARE ANALYSIS (combines all methods)
// ---------------------------------------------------------------------------
function analyseSpare(spare, {
  failureModes = [],
  equipments = [],
  weibullSets = [],
  demandHistory = [],
  missionTime = 8760,
  stockingPolicy = null
} = {}) {
  // 1. Link failure modes
  const linkedFMs = failureModes.filter(fm =>
    (fm.spareRequirement || '') === spare.id ||
    (fm.linkedSpares || []).includes(spare.id)
  );

  // 2. Link equipment via failure modes
  const linkedEquipmentIds = new Set();
  linkedFMs.forEach(fm => {
    if (fm.equipmentId) linkedEquipmentIds.add(fm.equipmentId);
  });
  const linkedEquipment = equipments.filter(e =>
    linkedEquipmentIds.has(e.id) ||
    e.spareReq === spare.id ||
    (e.spareReq && e.spareReq.includes(spare.id))
  );

  // 3. Criticality classification
  const criticalityTier = classifyCriticality(spare, linkedFMs);
  const policy = (stockingPolicy || DEFAULT_STOCKING_POLICY)[criticalityTier];

  // 4. Demand rate from failure modes
  const demandRate = linkedFMs.reduce((sum, fm) => sum + (parseFloat(fm.failureRate) || 0.001), 0);
  const expectedDemand = demandRate * missionTime;

  // 5. Current stock
  const currentStock = parseInt(spare.quantity) || parseInt(spare.stockLevel) || 0;
  const unitCost = parseFloat(spare.unitCost) || parseFloat(spare.cost) || 0;

  // 6. Poisson fill rate (keep for compatibility)
  const fillRate = poissonFillRate(expectedDemand, currentStock);
  const poissonRecommended = poissonRecommendedStock(expectedDemand, policy.serviceLevelPct / 100);

  // 7. Z-score ROP
  const rop = calculateROP(spare, demandHistory, policy.serviceLevelPct / 100, policy);

  // 8. Weibull demand (if data available)
  const weibullForecast = weibullDemandForecast(spare, linkedEquipment, weibullSets);

  // 9. Determine recommended stock (best available method)
  let recommendedStock, method;
  if (demandHistory.length >= 3) {
    recommendedStock = rop.results.reorderPoint;
    method = 'Z-score ROP';
  } else if (weibullForecast) {
    // Use Weibull monthly demand × lead time months + safety factor
    const monthlyDemand = parseFloat(weibullForecast.monthlyDemand);
    const leadTimeMonths = (parseFloat(spare.leadTime) || 30) / 30;
    recommendedStock = Math.ceil(monthlyDemand * leadTimeMonths * 1.5);
    method = 'Weibull forecast';
  } else {
    recommendedStock = poissonRecommended;
    method = 'Poisson';
  }

  // 10. Status
  const shortfall = Math.max(0, recommendedStock - currentStock);
  let status;
  if (fillRate >= policy.serviceLevelPct / 100) status = 'Adequate';
  else if (fillRate >= 0.80) status = 'Review';
  else status = 'Critical';

  return {
    spareId: spare.id,
    description: spare.name || spare.description || spare.id,
    criticalityTier,
    criticalityLabel: policy.label,
    serviceLevel: policy.serviceLevelPct,
    linkedEquipmentCount: linkedEquipment.length,
    linkedFailureModeCount: linkedFMs.length,
    demandRate,
    expectedDemand,
    currentStock,
    fillRate,
    fillRatePct: (fillRate * 100).toFixed(1),
    poissonRecommended,
    ropRecommended: rop.results.reorderPoint,
    safetyStock: rop.results.safetyStock,
    recommendedStock,
    method,
    shortfall,
    unitCost,
    annualHoldingCost: unitCost * Math.max(recommendedStock, currentStock),
    status,
    rop,
    weibullForecast,
    policy
  };
}

// ---------------------------------------------------------------------------
// OPTIMAL SPARES API BRIDGE
// Connects to spare.optimalworld.org / spares.optimalworld.org
// ---------------------------------------------------------------------------
class OptimalSparesAPI {
  constructor(baseUrl = 'https://spares.optimalworld.org/api/v1') {
    this.baseUrl = baseUrl;
    this.token = null;
    this.user = null;
    this.connected = false;
    this.lastError = null;
  }

  async _fetch(path, options = {}) {
    const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    try {
      const resp = await fetch(`${this.baseUrl}${path}`, { ...options, headers });
      if (resp.status === 401) {
        this.connected = false;
        this.token = null;
        throw new Error('Authentication expired. Please reconnect.');
      }
      if (!resp.ok) {
        const body = await resp.text();
        throw new Error(`API error ${resp.status}: ${body}`);
      }
      return await resp.json();
    } catch (err) {
      this.lastError = err.message;
      throw err;
    }
  }

  // --- Auth ---
  async login(username, password) {
    const data = await this._fetch('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password })
    });
    this.token = data.token;
    this.user = { id: data.user_id, username: data.username, role: data.role, fullName: data.full_name };
    this.connected = true;
    this.lastError = null;
    return this.user;
  }

  async verifyToken() {
    try {
      await this._fetch('/auth/verify');
      this.connected = true;
      return true;
    } catch {
      this.connected = false;
      return false;
    }
  }

  async healthCheck() {
    try {
      const data = await this._fetch('/health');
      return { online: true, ...data };
    } catch {
      return { online: false };
    }
  }

  // --- Materials ---
  async getMaterials(search = '', limit = 100) {
    return this._fetch(`/materials?search=${encodeURIComponent(search)}&limit=${limit}`);
  }

  async getMaterial(id) {
    return this._fetch(`/materials/${id}`);
  }

  async createMaterial(material) {
    return this._fetch('/materials', { method: 'POST', body: JSON.stringify(material) });
  }

  // --- Equipment BoM ---
  async getEquipmentList() {
    return this._fetch('/equipment/list');
  }

  async getEquipmentBom(nodeId) {
    return this._fetch(`/equipment/${encodeURIComponent(nodeId)}/bom`);
  }

  async addBomItem(item) {
    return this._fetch('/equipment/bom', { method: 'POST', body: JSON.stringify(item) });
  }

  async updateBomItem(bomId, updates) {
    return this._fetch(`/equipment/bom/${bomId}`, { method: 'PUT', body: JSON.stringify(updates) });
  }

  // --- Inventory & ROP ---
  async getReorderBreakdown(materialId) {
    return this._fetch(`/inventory/${materialId}/reorder-breakdown`);
  }

  async getStrategicInventory() {
    return this._fetch('/inventory/strategic');
  }

  // --- Stocking Policy ---
  async getStockingPolicyMatrix() {
    try {
      return await this._fetch('/stocking-policy/matrix');
    } catch {
      return DEFAULT_STOCKING_POLICY; // Fallback to local defaults
    }
  }

  async updateStockingPolicyMatrix(matrix) {
    return this._fetch('/stocking-policy/matrix', { method: 'PUT', body: JSON.stringify(matrix) });
  }

  async applyPolicyToAsset(assetId) {
    return this._fetch(`/stocking-policy/apply/${assetId}`, { method: 'POST' });
  }

  // --- Analytics ---
  async getSpendCube(dimension = 'category', periodMonths = 12) {
    return this._fetch(`/analytics/spend-cube?dimension=${dimension}&period_months=${periodMonths}`);
  }

  // --- Failure demand ---
  async getFailureDemand(equipmentNodeId, failureMode, urgency = 'planned') {
    return this._fetch('/equipment/failure-demand', {
      method: 'POST',
      body: JSON.stringify({ equipment_node_id: equipmentNodeId, failure_mode: failureMode, urgency })
    });
  }

  // --- Verticals ---
  async getVerticalProfile(code = 'oil_gas') {
    return this._fetch(`/verticals/${code}`);
  }

  // --- Push equipment from OptimalAV to OptimalSPARES ---
  async syncEquipmentBom(equipment, spares, failureModes) {
    const results = { pushed: 0, errors: [] };
    for (const eq of equipment) {
      try {
        // Find spares linked to this equipment via failure modes
        const linkedFMs = failureModes.filter(fm => fm.equipmentId === eq.id);
        const linkedSpareIds = new Set();
        linkedFMs.forEach(fm => {
          if (fm.spareRequirement) linkedSpareIds.add(fm.spareRequirement);
          (fm.linkedSpares || []).forEach(sid => linkedSpareIds.add(sid));
        });

        for (const spareId of linkedSpareIds) {
          const spare = spares.find(s => s.id === spareId);
          if (!spare) continue;

          // Determine criticality from failure modes
          const criticalityTier = classifyCriticality(spare, linkedFMs.filter(fm =>
            fm.spareRequirement === spareId || (fm.linkedSpares || []).includes(spareId)
          ));
          const critMap = { safety_critical: 'safety', production_critical: 'production', standard: 'economic', non_critical: 'none' };

          await this.addBomItem({
            equipment_node_id: eq.id,
            equipment_name: eq.description || eq.id,
            material_id: spare.id,
            qty_per_assembly: parseFloat(spare.qtyPerAssembly) || 1,
            criticality: critMap[criticalityTier] || 'none',
            notes: `Synced from OptimalAV — ${linkedFMs.length} failure modes`
          });
          results.pushed++;
        }
      } catch (err) {
        results.errors.push({ equipmentId: eq.id, error: err.message });
      }
    }
    return results;
  }
}

// ---------------------------------------------------------------------------
// EXPORTS
// ---------------------------------------------------------------------------
export {
  OptimalSparesAPI,
  calculateROP,
  weibullDemandForecast,
  classifyCriticality,
  analyseSpare,
  poissonFillRate,
  poissonRecommendedStock,
  getZScore,
  DEFAULT_STOCKING_POLICY,
  Z_LOOKUP
};
